/**
 * Custom video player used inside tech-feature modals. Replaces the browser's
 * native <video> controls with a Figma-matching UI (play/pause, volume, time,
 * progress, fullscreen). When the video ends, an "end card" overlay is shown
 * with the current PDP product's info and a "watch again" link.
 *
 * Markup contract (see snippets/tech-feature-video-player.liquid):
 *   <tech-feature-video-player>
 *     <video data-tfvp-video>...</video>
 *     <button data-tfvp-play-pause></button>
 *     <button data-tfvp-mute></button>
 *     <input type="range" data-tfvp-scrubber>
 *     <span data-tfvp-time-current></span>
 *     <span data-tfvp-time-total></span>
 *     <button data-tfvp-fullscreen></button>
 *     <div data-tfvp-end-card hidden>
 *       <button data-tfvp-watch-again></button>
 *     </div>
 *     <button data-tfvp-close></button>
 *   </tech-feature-video-player>
 */

const SVG_PLAY = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M7 5v14l11-7z"/></svg>';
const SVG_PAUSE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M7 4h4v16H7zM13 4h4v16h-4z"/></svg>';
const SVG_VOLUME_ON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.23v2.06A7 7 0 0 1 19 12a7 7 0 0 1-5 6.71v2.06A9 9 0 0 0 21 12a9 9 0 0 0-7-8.77z"/></svg>';
const SVG_VOLUME_OFF = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M16.5 12A4.5 4.5 0 0 0 14 8v2.18L16.45 12.63A4.5 4.5 0 0 0 16.5 12zM19 12a7 7 0 0 1-.42 2.4l1.5 1.5A8.97 8.97 0 0 0 21 12a9 9 0 0 0-7-8.77v2.06A7 7 0 0 1 19 12zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25-.17.03v2.06a8.95 8.95 0 0 0 3.69-1.6L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>';
/* The modal IS the fullscreen experience, so this button always shows the
   "exit fullscreen" icon (arrows pointing inward) and clicking it closes the
   dialog (returning the user to the page). */
const SVG_FULLSCREEN_EXIT = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>';

class TechFeatureVideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-tfvp-video]');
    if (!this.video) return;

    this.playPauseBtn = this.querySelector('[data-tfvp-play-pause]');
    this.muteBtn = this.querySelector('[data-tfvp-mute]');
    this.scrubber = this.querySelector('[data-tfvp-scrubber]');
    this.timeCurrent = this.querySelector('[data-tfvp-time-current]');
    this.timeTotal = this.querySelector('[data-tfvp-time-total]');
    this.fullscreenBtn = this.querySelector('[data-tfvp-fullscreen]');
    this.endCard = this.querySelector('[data-tfvp-end-card]');
    this.watchAgainBtn = this.querySelector('[data-tfvp-watch-again]');
    this.closeBtn = this.querySelector('[data-tfvp-close]');
    this.surface = this.querySelector('[data-tfvp-surface]');

    this.#paintPlayPauseIcon(false);
    this.#paintMuteIcon();
    this.#paintCloseFullscreenIcon();

    this.video.addEventListener('play', this.#onPlay);
    this.video.addEventListener('pause', this.#onPause);
    this.video.addEventListener('volumechange', this.#paintMuteIcon);
    this.video.addEventListener('timeupdate', this.#onTimeUpdate);
    this.video.addEventListener('loadedmetadata', this.#onLoadedMetadata);
    this.video.addEventListener('ended', this.#onEnded);

    if (this.playPauseBtn) this.playPauseBtn.addEventListener('click', this.#togglePlay);
    if (this.muteBtn) this.muteBtn.addEventListener('click', this.#toggleMute);
    if (this.scrubber) this.scrubber.addEventListener('input', this.#onScrub);
    if (this.fullscreenBtn) this.fullscreenBtn.addEventListener('click', this.#closeParentDialog);
    if (this.watchAgainBtn) this.watchAgainBtn.addEventListener('click', this.#watchAgain);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.#closeParentDialog);

    // Click anywhere on the video toggles play/pause; moving the pointer (or
    // leaving) controls the auto-hide of the UI.
    if (this.surface) this.surface.addEventListener('click', this.#onSurfaceClick);
    this.addEventListener('pointermove', this.#showControls);
    this.addEventListener('pointerleave', this.#onPointerLeave);


    /* Auto-detect video aspect ratio once metadata is available so the layout
       can adapt to 9/16 vs 16/9 vs other formats. */
    if (this.video.readyState >= 1) this.#onLoadedMetadata();
  }

  disconnectedCallback() {
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.removeEventListener('pointermove', this.#showControls);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    if (this.surface) this.surface.removeEventListener('click', this.#onSurfaceClick);
    if (!this.video) return;
    this.video.removeEventListener('play', this.#onPlay);
    this.video.removeEventListener('pause', this.#onPause);
    this.video.removeEventListener('volumechange', this.#paintMuteIcon);
    this.video.removeEventListener('timeupdate', this.#onTimeUpdate);
    this.video.removeEventListener('loadedmetadata', this.#onLoadedMetadata);
    this.video.removeEventListener('ended', this.#onEnded);
  }

  /* Auto-hide timer for the controls/header during playback. */
  #hideTimer = null;

  /* Set right after start() so the click that opened the modal doesn't bleed
     through to the stage and immediately pause the video. */
  #suppressSurfaceToggle = false;

  #onSurfaceClick = () => {
    // Only block an immediate PAUSE of a video that just auto-started (the
    // open gesture bleeding through). Never block a click meant to start it.
    if (this.#suppressSurfaceToggle && this.video && !this.video.paused) {
      this.#suppressSurfaceToggle = false;
      return;
    }
    this.#suppressSurfaceToggle = false;
    this.#togglePlay();
  };

  #showControls = () => {
    this.classList.remove('tfvp--idle');
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    if (this.video && !this.video.paused && !this.video.ended) {
      this.#hideTimer = setTimeout(this.#hideControls, 2500);
    }
  };

  #hideControls = () => {
    if (this.video && !this.video.paused && !this.video.ended) {
      this.classList.add('tfvp--idle');
    }
  };

  #onPointerLeave = () => {
    if (this.video && !this.video.paused && !this.video.ended) {
      this.classList.add('tfvp--idle');
    }
  };

  /* Called by the parent dialog handler when the modal opens. */
  start() {
    if (!this.video) return;
    this.#hideEndCard();
    this.video.currentTime = 0;
    this.#suppressSurfaceToggle = true;
    setTimeout(() => {
      this.#suppressSurfaceToggle = false;
    }, 400);
    const playPromise = this.video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* Autoplay blocked — user can press play manually. */
      });
    }
  }

  /* Called by the parent dialog handler when the modal closes. */
  stop() {
    if (!this.video) return;
    if (typeof this.video.pause === 'function') this.video.pause();
    this.#hideEndCard();
  }

  #togglePlay = () => {
    if (this.video.paused || this.video.ended) {
      this.#hideEndCard();
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  };

  #toggleMute = () => {
    this.video.muted = !this.video.muted;
  };

  #onScrub = () => {
    if (!this.video.duration || !isFinite(this.video.duration)) return;
    const percent = Number(this.scrubber.value);
    this.video.currentTime = (percent / 100) * this.video.duration;
  };

  #watchAgain = () => {
    this.#hideEndCard();
    this.video.currentTime = 0;
    this.video.play().catch(() => {});
  };

  #closeParentDialog = () => {
    const dialog = this.closest('dialog');
    if (dialog && typeof dialog.close === 'function') dialog.close();
  };

  #onPlay = () => {
    this.#paintPlayPauseIcon(true);
    this.#hideEndCard();
    this.classList.add('tfvp--playing');
    this.#showControls();
  };

  #onPause = () => {
    this.#paintPlayPauseIcon(false);
    // Paused → always show the controls and the centre play button.
    this.classList.remove('tfvp--playing');
    this.classList.remove('tfvp--idle');
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
  };

  #onLoadedMetadata = () => {
    if (this.timeTotal && isFinite(this.video.duration)) {
      this.timeTotal.textContent = formatTime(this.video.duration);
    }
    /* Expose the aspect ratio so CSS can size the video element. */
    if (this.video.videoWidth && this.video.videoHeight) {
      const ratio = this.video.videoWidth / this.video.videoHeight;
      this.style.setProperty('--tfvp-aspect', `${ratio}`);
      this.dataset.aspect = ratio >= 1 ? 'landscape' : 'portrait';
    }
  };

  #onTimeUpdate = () => {
    if (!this.video.duration || !isFinite(this.video.duration)) return;
    if (this.scrubber) {
      const percent = (this.video.currentTime / this.video.duration) * 100;
      this.scrubber.value = String(percent);
      this.scrubber.style.setProperty('--tfvp-progress', `${percent}%`);
    }
    if (this.timeCurrent) {
      this.timeCurrent.textContent = formatTime(this.video.currentTime);
    }
  };

  #onEnded = () => {
    this.#paintPlayPauseIcon(false);
    this.classList.remove('tfvp--playing');
    this.classList.remove('tfvp--idle');
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#showEndCard();
  };

  #showEndCard = () => {
    if (this.endCard) this.endCard.hidden = false;
  };

  #hideEndCard = () => {
    if (this.endCard) this.endCard.hidden = true;
  };

  #paintPlayPauseIcon = (isPlaying) => {
    if (!this.playPauseBtn) return;
    this.playPauseBtn.innerHTML = isPlaying ? SVG_PAUSE : SVG_PLAY;
    this.playPauseBtn.setAttribute(
      'aria-label',
      this.playPauseBtn.dataset[isPlaying ? 'labelPause' : 'labelPlay'] || (isPlaying ? 'Pause' : 'Play')
    );
  };

  #paintMuteIcon = () => {
    if (!this.muteBtn) return;
    const muted = this.video.muted || this.video.volume === 0;
    this.muteBtn.innerHTML = muted ? SVG_VOLUME_OFF : SVG_VOLUME_ON;
    this.muteBtn.setAttribute(
      'aria-label',
      this.muteBtn.dataset[muted ? 'labelUnmute' : 'labelMute'] || (muted ? 'Unmute' : 'Mute')
    );
  };

  #paintCloseFullscreenIcon = () => {
    if (!this.fullscreenBtn) return;
    this.fullscreenBtn.innerHTML = SVG_FULLSCREEN_EXIT;
    this.fullscreenBtn.setAttribute(
      'aria-label',
      this.fullscreenBtn.dataset.labelExitFullscreen || 'Exit fullscreen'
    );
  };
}

function formatTime(seconds) {
  if (!isFinite(seconds)) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

if (!customElements.get('tech-feature-video-player')) {
  customElements.define('tech-feature-video-player', TechFeatureVideoPlayer);
}
